import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Read-only admin view of notification eligibility, server-derived by the canonical
 * classifier (admin-notification-eligibility edge function). No client-side
 * reinvention of classifier logic — this hook only fetches + renders.
 */
export interface PropertyEligibilityRow {
  property_id: string;
  property_name: string | null;
  provider: string | null;
  is_hidden: boolean;
  onboarding_complete: boolean;
  readiness_state: string | null;
  reachability: string;
  provider_incident_active: boolean;
  primary_bucket: string;
  secondary_reason_codes: string[];
  soc_validity: string;
  battery_presence: string;
  battery_capacity_resolved: boolean;
  latest_soc: number | null;
  reserve_kwh: number | null;
  grid_mode: string | null;
  battery_eligible: boolean;
  battery_severity: string | null;
  forecast_eligible: boolean;
  verified_low_battery: boolean;
  active_alert_states: Array<{ id: string; event_kind: string; activated_at: string | null }>;
  last_delivered_notification: { event_kind: string; created_at: string; outcome: string } | null;
  customer_ineligible: boolean;
}

export interface AdminNotificationEligibility {
  org_id: string;
  properties: PropertyEligibilityRow[];
}

async function fetchEligibility(orgId: string): Promise<AdminNotificationEligibility> {
  const { data, error } = await supabase.functions.invoke('admin-notification-eligibility', {
    body: { org_id: orgId },
  });
  if (error) throw error;
  return data as AdminNotificationEligibility;
}

export function useAdminNotificationEligibility(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-notification-eligibility', orgId],
    queryFn: () => fetchEligibility(orgId as string),
    enabled: enabled && !!orgId,
    staleTime: 60_000,
  });
}
